import { type InputRef, Button, Form, Input, Modal } from 'antd';
import { useRef } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { MessageType, ModalActionButtons, useMessage } from 'components/ui';
import { AutoFocus } from 'horizon/components/AutoFocus';
import { formatDamageId, getObservationsFromDamage } from 'horizon/components/Damages/utils';
import {
  type AtlasGqlDamageSearchQuery,
  type AtlasGqlHorizonDamage,
  AtlasGqlDamageSearchMatch,
  AtlasGqlFilterOperator,
  useDamageSearchLazyQuery,
  useGetLegacyDamagesForSearchLazyQuery,
} from 'types/atlas-graphql';
import { idRegex as idRegexString, uuidRegex as uuidRegexString } from 'utils/constants';

import { StyledList } from './DamageSearch.style';

type DamageSearchProps = { isOpen: boolean; onClose: () => void };

export const DamageSearch: React.FunctionComponent<DamageSearchProps> = ({ isOpen, onClose }) => {
  const { t } = useTranslation();
  const message = useMessage();
  const inputRef = useRef<InputRef>(null);
  const history = useHistory();

  const [form] = Form.useForm();

  const uuidRegex = new RegExp(`^${uuidRegexString}$`, 'i');
  const idRegex = new RegExp(`^${idRegexString}$`, 'i');

  const damageLink = (damageId: string) => (
    <a href={`/damages2/${damageId}`} target="_blank" rel="noreferrer">
      {formatDamageId({ id: damageId })}
    </a>
  );

  // query to search for a valid horizon damage
  const [damageSearch, { loading: damageSearchLoading }] = useDamageSearchLazyQuery({
    fetchPolicy: 'cache-and-network',
  });

  const handleDamageSearchResult = (
    data: AtlasGqlDamageSearchQuery,
    search: string,
    displayInput: string
  ) => {
    const results = data.damageSearch?.results ?? [];
    const resultSearchRegex = new RegExp(`^${search}`, 'i');

    const processedResults = results.reduce(
      (
        acc: {
          damageId: string;
          observationId?: string;
          message: JSX.Element;
        }[],
        result
      ) => {
        const { match, damage } = result ?? {};
        if (match && damage) {
          const observations = getObservationsFromDamage(damage as AtlasGqlHorizonDamage);

          switch (match) {
            // horizon damage found
            case AtlasGqlDamageSearchMatch.HorizonDamageUuid:
            case AtlasGqlDamageSearchMatch.HorizonDamageId:
              acc.push({
                damageId: damage.id,
                message: (
                  <Trans
                    i18nKey="damage.searchResultDamage"
                    t={t}
                    values={{ displayInput }}
                    components={{ damageLink: damageLink(damage.id) }}
                    defaults="'{{displayInput}}' is a Damage ID/UUID and corresponds to Damage <damageLink/>"
                  />
                ),
              });
              break;

            // horizon observation found
            case AtlasGqlDamageSearchMatch.HorizonDamageObservationUuid:
            case AtlasGqlDamageSearchMatch.HorizonDamageObservationId:
              acc.push({
                damageId: damage.id,
                observationId: observations.find(o => resultSearchRegex.test(o.id))?.id,
                message: (
                  <Trans
                    i18nKey="damage.searchResultObservation"
                    t={t}
                    values={{ displayInput }}
                    components={{ damageLink: damageLink(damage.id) }}
                    defaults="'{{displayInput}}' is an Observation ID/UUID and belongs to Damage <damageLink/>"
                  />
                ),
              });
              break;

            case AtlasGqlDamageSearchMatch.MergedHorizonDamageUuid:
            case AtlasGqlDamageSearchMatch.MergedHorizonDamageId:
              acc.push({
                damageId: damage.id,
                message: (
                  <Trans
                    i18nKey="damage.searchResultMergedDamage"
                    t={t}
                    values={{ displayInput }}
                    components={{ damageLink: damageLink(damage.id) }}
                    defaults="'{{displayInput}}' is a Damage ID/UUID and has been linked to Damage <damageLink/>"
                  />
                ),
              });
              break;

            // legacy damage found
            case AtlasGqlDamageSearchMatch.LegacyDamageUuid:
            case AtlasGqlDamageSearchMatch.LegacyDamageId:
              acc.push({
                damageId: damage.id,
                observationId: observations.find(o => o.legacyDamageBranchId === search)?.id,
                message: (
                  <Trans
                    i18nKey="damage.searchResultLegacyDamage"
                    t={t}
                    values={{ displayInput }}
                    components={{ damageLink: damageLink(damage.id) }}
                    defaults="'{{displayInput}}' is a Legacy Damage ID/UUID and belongs to Damage <damageLink/>"
                  />
                ),
              });
              break;

            default:
              break;
          }
        }

        return acc;
      },
      []
    );

    if (processedResults.length > 0) {
      // results have been found
      if (processedResults.length === 1) {
        // single damage found -> notify user and redirect
        const { damageId, observationId, message: successMessage } = processedResults[0];
        message.success(successMessage, 4);
        navigateToDamage({ damageId, observationId });
      } else if (processedResults.length > 1) {
        // multiple damages found -> notify user but do not redirect
        message.withClose({
          key: 'damage-search',
          type: MessageType.INFO,
          content: (
            <>
              <span>{t('damage.multipleResultsFound', 'Multiple results found:')}</span>
              <StyledList>
                {processedResults.map((result, index) => (
                  <li key={index}>{result.message}</li>
                ))}
              </StyledList>
            </>
          ),
        });
      }
    } else {
      // no results have been found
      if (idRegex.test(search)) {
        // load legacy damage
        loadLegacyDamages(displayInput);
      } else {
        // no valid search results found
        message.error(
          t('damage.searchNotRecognized', "'{{input}}' was not recognized by the system.", {
            input: displayInput,
          }),
          4
        );
      }
    }
  };

  // query to get legacy damages
  const [getLegacyDamages, { loading: legacyDamagesLoading }] =
    useGetLegacyDamagesForSearchLazyQuery({
      fetchPolicy: 'cache-and-network',
    });

  // load horizon damages for the search string
  const loadDamageSearch = (search: string, displayInput: string) => {
    damageSearch({ variables: { input: { search } } }).then(result => {
      if (result.data) {
        handleDamageSearchResult(result.data, search, displayInput);
      }
    });
  };

  // load legacy damages to get the legacyDamageBranchId
  const loadLegacyDamages = (search: string) => {
    getLegacyDamages({
      variables: {
        offset: 0,
        limit: 10,
        filterBy: [
          {
            key: 'number',
            values: [search],
            operator: AtlasGqlFilterOperator.Equals,
          },
        ],
        sortBy: [],
      },
    }).then(result => {
      if (result.data) {
        const { id: legacyDamageBranchId } = result.data.damages?.items?.[0] ?? {};

        if (legacyDamageBranchId) {
          // legacy damage found -> search horizon damages with this id
          loadDamageSearch(legacyDamageBranchId, search);
        } else {
          // no legacy damage found -> error
          message.error(
            t('damage.searchNotRecognized', "'{{input}}' was not recognized by the system.", {
              input: search,
            }),
            4
          );
        }
      }
    });
  };

  // redirect to damage page
  const navigateToDamage = ({
    damageId,
    observationId,
  }: {
    damageId: string;
    observationId?: string;
  }) => {
    setTimeout(() => {
      history.push(`/damages2/${damageId}${observationId ? `?observation=${observationId}` : ''}`);
    }, 2000);
  };

  // handle submit logic
  const handleSubmit = ({ search }: { search: string }) => {
    loadDamageSearch(search, search);
  };

  // handler to close and reset form
  const onFormClose = () => {
    form.resetFields();
    onClose();
  };

  return (
    <Modal
      title={t('table.idSearch', 'ID Search')}
      open={isOpen}
      onCancel={onFormClose}
      destroyOnHidden
      footer={null}
    >
      <AutoFocus elementToFocus={inputRef}>
        <Form form={form} layout="vertical" onFinish={handleSubmit}>
          <Form.Item
            name="search"
            label={t('damage.searchIdLabel', 'Damage, Observation, or Legacy Damage ID/UUID')}
            rules={[
              {
                required: true,
                message: t('components.inputRequired', 'Input is required.'),
                validateTrigger: 'onFinish',
              },
              {
                pattern: new RegExp(idRegex.source + '|' + uuidRegex.source, 'i'),
                message: (
                  <>
                    <span>{t('damage.enterValidInput', 'Please enter a valid input:')}</span>
                    <StyledList>
                      <li>{t('damage.idExample', 'ID (ex. A63A2315 or 5XJ9GNMS-7)')}</li>
                      <li>
                        {t('damage.uuidExample', 'UUID (ex. a63a2315-73c5-45cd-b108-5310a362839b)')}
                      </li>
                    </StyledList>
                  </>
                ),
                validateTrigger: 'onFinish',
              },
            ]}
          >
            <Input ref={inputRef} placeholder={t('damage.enterIdOrUuid', 'Enter an ID or UUID')} />
          </Form.Item>
          <ModalActionButtons>
            <Button onClick={onFormClose}>{t('common.cancel', 'Cancel')}</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={legacyDamagesLoading || damageSearchLoading}
            >
              {t('common.search', 'Search')}
            </Button>
          </ModalActionButtons>
        </Form>
      </AutoFocus>
    </Modal>
  );
};
